// src/context/ProjectContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useProjectsData } from '../hooks/useProjectsData';
import { useDocumentPolling } from '../hooks/useDocumentPolling';
import { getJsonLocalForage, setJsonLocalForage } from '../utils/localForageUtils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { safeIndexedDBWrite, onVisibilityChange } from '../utils/mobileOptimizations';

// Create the context
const ProjectContext = createContext(null);

/**
 * Provider component for project state and operations
 */
export function ProjectProvider({ children }) {
  // Use our custom hook for project data
  const {
    projects,
    loading,
    error,
    refreshProjects,
    refreshPhaseFiles,
    onCreateProject,
    onRenameProject,
    onDeleteProject,
    updateDocInProjects,
    getKbIdForFile,
    setProjects,
    getDocCacheRef,
    deleteDocumentInProject,
    renameDocumentInProject,
    runDocumentsInProject
  } = useProjectsData();
  
  // Track the active project
  const [activeProjectId, setActiveProjectId] = useState(null);
  
  // Track page visibility
  const [isPageVisible, setIsPageVisible] = useState(true);

  // Load the active project ID from localForage on mount, but only if no project param in URL
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');

  useEffect(() => {
    async function loadActiveProjectId() {
      if (projectIdFromUrl) return; // Let the URL control project selection
      const savedId = await getJsonLocalForage('activeProjectId');
      if (savedId) {
        setActiveProjectId(savedId);
      } else if (projects.length > 0) {
        setActiveProjectId(projects[0].id);
      }
    }
    loadActiveProjectId();
  }, [projects, projectIdFromUrl]);

  // Listen for visibility changes
  useEffect(() => {
    const cleanup = onVisibilityChange((isHidden) => {
      setIsPageVisible(!isHidden);
    });
    return cleanup;
  }, []);

  // Save the active project ID to localForage whenever it changes
  // With debouncing and visibility check
  useEffect(() => {
    if (!activeProjectId || !isPageVisible) return;
    
    const timeoutId = setTimeout(() => {
      safeIndexedDBWrite(() => setJsonLocalForage('activeProjectId', activeProjectId))
        .catch(err => {
          console.error('[ProjectContext] Failed to cache activeProjectId:', err);
        });
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [activeProjectId, isPageVisible]);

  // Ensure activeProjectId is valid after projects change
  useEffect(() => {
    if (activeProjectId && projects.length > 0) {
      const stillExists = projects.some(p => p.id === activeProjectId);
      if (!stillExists) {
        setActiveProjectId(null);
      }
    }
  }, [projects, activeProjectId]);

  // Get the active project object
  const getActiveProject = () => {
    if (!activeProjectId) return null;
    return projects.find(p => p.id === activeProjectId) || null;
  };
  
  // Use the document polling hook to automatically poll processing documents
  useDocumentPolling({
    projects,
    updateDocInProjects,
    getKbIdForFile
  });
  
  // Provide all project-related state and functions to children
  const value = {
    // State
    projects,
    loading,
    error,
    setProjects,
    
    // Active project
    activeProjectId,
    setActiveProjectId,
    getActiveProject,
    
    // Actions
    refreshProjects,
    refreshPhaseFiles,
    createProject: onCreateProject,
    renameProject: onRenameProject,
    deleteProject: onDeleteProject,
    updateDocInProjects,
    getKbIdForFile,
    getDocCacheRef,
    
    // Centralized document operations
    deleteDocumentInProject,
    renameDocumentInProject,
    runDocumentsInProject
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

/**
 * Custom hook to use the project context
 */
export function useProjectContext() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
} 